/**
 * Frontend Timezone Utilities
 * Handles timezone conversions and display for the admin interface
 */

// Store timezone configuration (matching backend)
const STORE_TIMEZONES: Record<string, string> = {
  'olney': 'America/New_York',           // Olney, MD (EST/EDT)
  'stamford': 'America/New_York',        // Stamford, CT (EST/EDT)
  'west_hartford': 'America/New_York',   // West Hartford, CT (EST/EDT)
  'voorhees': 'America/New_York',        // Voorhees, NJ (EST/EDT)
  'exeter': 'America/New_York',          // Exeter, RI (EST/EDT)
  'essex_junction': 'America/New_York',  // Essex Junction, VT (EST/EDT)
  'hamilton': 'America/New_York',        // Hamilton, NJ (EST/EDT)
  '4855': 'America/New_York',            // Voorhees Medical (EST/EDT)
  '5440': 'America/New_York',            // Olney Recreational (EST/EDT)
  '696002abeb54a2091b709e0f': 'America/New_York'  // Hamilton Recreational (EST/EDT)
};

/**
 * Get timezone for a store by ID
 */
export const getStoreTimezone = (storeId: string): string => {
  const timezone = STORE_TIMEZONES[storeId];
  if (!timezone) {
    return 'America/New_York';
  }
  return timezone;
};

/**
 * Get timezone abbreviation (EST, EDT, etc.)
 */
export const getTimezoneAbbreviation = (storeId: string, date?: Date): string => {
  const timezone = getStoreTimezone(storeId);
  const testDate = date || new Date();
  
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short'
  });
  
  const parts = formatter.formatToParts(testDate);
  const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;
  
  return timeZoneName || 'UTC';
};

/**
 * Format a date for display in store timezone
 */
export const formatDateInStoreTimezone = (
  date: Date, 
  storeId: string, 
  options?: Intl.DateTimeFormatOptions
): string => {
  const timezone = getStoreTimezone(storeId);
  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...options
  };
  
  return date.toLocaleDateString('en-US', defaultOptions);
};

/**
 * Get current date/time in store timezone (same moment, just represented in store tz)
 */
export const getNowInStoreTimezone = (storeId: string): Date => {
  // Return current UTC time - "now" is the same moment everywhere
  return new Date();
};

/**
 * Convert a UTC date to store timezone for display
 */
export const convertUtcToStoreTimezone = (utcDate: Date, storeId: string): Date => {
  const timezone = getStoreTimezone(storeId);
  
  // Get what time it would show on a clock in that timezone
  const timeInTimezone = utcDate.toLocaleString("sv-SE", { timeZone: timezone });
  
  // Create a Date object representing that local time
  return new Date(timeInTimezone);
};

/**
 * Get date string in YYYY-MM-DD format for HTML input in store timezone
 */
export const getDateInputValue = (date: Date, storeId: string): string => {
  const storeDate = convertUtcToStoreTimezone(date, storeId);
  // Use local date parts, not toISOString() which converts back to UTC
  const year = storeDate.getFullYear();
  const month = String(storeDate.getMonth() + 1).padStart(2, '0');
  const day = String(storeDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get time string in HH:MM format for HTML input in store timezone
 */
export const getTimeInputValue = (date: Date, storeId: string): string => {
  const storeDate = convertUtcToStoreTimezone(date, storeId);
  const hours = storeDate.getHours().toString().padStart(2, '0');
  const minutes = storeDate.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Combine HTML date and time inputs into a Date object, interpreting as store timezone
 */
export const combineDateTimeInputs = (
  dateStr: string, // YYYY-MM-DD format
  timeStr: string, // HH:MM format
  storeId: string
): Date => {
  // All stores use America/New_York (Eastern time)
  // User inputs time in Eastern, we need to convert to UTC for storage
  
  // Simple approach: Use Date constructor with UTC, then adjust for Eastern offset
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  
  // September 4, 2025 is during EDT (Eastern Daylight Time) = UTC-4
  // We need to determine if this date is during EST (-5) or EDT (-4)
  
  // Create a test date to check DST status
  const testDate = new Date(year, month - 1, day);
  
  // Check if this date observes DST by seeing what the timezone offset is
  // Create two dates: one formatted in Eastern, one in UTC, compare
  const easternOffset = testDate.toLocaleString('en-US', { 
    timeZone: 'America/New_York',
    timeZoneName: 'longOffset' 
  }).match(/GMT([+-]\d+)/)?.[1];
  
  // Parse the offset (e.g., "-4" from "GMT-4")
  let offsetHours = 4; // Default to EDT
  if (easternOffset) {
    offsetHours = Math.abs(parseInt(easternOffset));
  }
  
  // Create the UTC date: if it's 4:40 PM Eastern and Eastern is UTC-4, then UTC is 8:40 PM
  return new Date(Date.UTC(year, month - 1, day, hour + offsetHours, minute, 0, 0));
};

/**
 * Check if user's browser timezone differs from store timezone
 */
export const isBrowserTimzoneDifferent = (storeId: string): boolean => {
  const storeTimezone = getStoreTimezone(storeId);
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return browserTimezone !== storeTimezone;
};

/**
 * Get browser timezone for comparison
 */
export const getBrowserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Check if a date is in the past relative to store timezone
 */
export const isDateInPast = (date: Date, storeId: string): boolean => {
  const now = new Date();
  return date <= now; // Simple UTC comparison
};

/**
 * Check if two date ranges overlap
 */
export const dateRangesOverlap = (
  start1: Date, end1: Date,
  start2: Date, end2: Date
): boolean => {
  return start1 < end2 && start2 < end1;
};

/**
 * Format date range for display in store timezone
 */
export const formatDateRange = (startDate?: Date, endDate?: Date, storeId?: string): string => {
  if (!startDate || !endDate || !storeId) return '';
  
  const tzAbbr = getTimezoneAbbreviation(storeId, startDate);
  
  const start = formatDateInStoreTimezone(startDate, storeId);
  
  // Check if same day
  const startDay = formatDateInStoreTimezone(startDate, storeId, { 
    timeZone: getStoreTimezone(storeId), 
    weekday: 'short', month: 'short', day: 'numeric' 
  });
  const endDay = formatDateInStoreTimezone(endDate, storeId, { 
    timeZone: getStoreTimezone(storeId), 
    weekday: 'short', month: 'short', day: 'numeric' 
  });
  
  if (startDay === endDay) {
    const end = formatDateInStoreTimezone(endDate, storeId, {
      timeZone: getStoreTimezone(storeId),
      hour: 'numeric',
      minute: '2-digit', 
      hour12: true
    });
    return `${start} → ${end} ${tzAbbr}`;
  } else {
    const endFull = formatDateInStoreTimezone(endDate, storeId);
    return `${start} → ${endFull} ${tzAbbr}`;
  }
};

/**
 * Debug logging for timezone operations (no-op in production)
 */
export const logTimezoneDebug = (operation: string, storeId: string, dates: { [key: string]: Date }) => {
  // Debug logging disabled in production
};